import {
  Button,
  InputAdornment,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import {t} from 'i18next';
import React, {useEffect, useId, useMemo, useState, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReactComponent as DropDownIcon} from '../../../../src/assests/svg/down_arrow.svg';
import {ReactComponent as DropUpIcon} from '../../../../src/assests/svg/up_arrow.svg';
import {selectOption, setOptions} from '../../../redux/slices/clientSlice';
import {RootState} from '../../../redux/store';
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {AurTextField} from '../../common/aur-text-field/AurTextField';
import {CLIENT_DROP_DOWN_ID} from '../../layouts/AurClientDropDownWrapper';
import {useNavigate} from 'react-router-dom';
import styles from './style.module.scss';
import {useFieldFocusStyle} from '../../../hooks/utils/useFieldFocusStyle';
import {PROJECT_LIST_SEARCH_ENABLE_COUNT} from '../../../constants/constants';

const CustomMenu = styled(Menu)(() => ({
  '& .MuiPaper-root': {
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '16px',
    },
    '&::-webkit-scrollbar-track': {
      marginTop: '2px',
      marginBottom: '2px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#546A8C',
      borderRadius: '100px',
      border: '6px solid transparent',
      backgroundClip: 'content-box',
    },
  },
}));
interface ClientDropDownProps {
  buttonOrTextField: string;
  data?: Array<Record<string, any>>; // Accepts any object type
  displayKey: string; // Key to display in dropdown
  idKey: string; // Key to use as the identifier
  buttonLabel?: string;
  dropDownID: string;
  redirectOnItemChange?: string;
  buttonBorderHighlight?: boolean;
  selectDefaultFirstItem?: boolean;
  initialSelectedOptionId?: string; // Set intitial selected option id if already have option selected instead of first element in option
  onOptionPick?: (optionId: string) => void; // CB for send option selected event to parent component
  searchReqLoading?: boolean; // For show loader while input search api call running
  onSearchInput?: (input: string) => void; // Parent get search input from this cb for perform search on server side using api and local search got disable
  disableDropDownForSingleItem?: boolean; // set true if need to disable dropdown show if only have one option to display
  enableSearch?: boolean; // If value passed then search visible on this else by default more than 12 option for menu then search enabled
  hintToViewOnSelection?: string; // Pass string to view when option selected like if showing invoices on selection then pass value *invoices*
  dataTestId?: string;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  isSortedAlphabetically?: boolean;
  disableDropDown?: string;
  resetField?: boolean;
  ariaLabel?: string;
  ariaDescription?: string;
  initialLoading?: boolean;
  allowAutoComplete?: boolean;
}

const MapAccessibilityContentKey: {[key: string]: string} = {
  ModalProject: 'project',
  Workspace: 'workspace',
  Project: 'project',
  Clients: 'client',
};

const AurClientDropdown = (props: ClientDropDownProps) => {
  const {
    buttonOrTextField = 'button',
    data,
    displayKey,
    idKey,
    buttonLabel,
    dropDownID,
    buttonBorderHighlight = false,
    selectDefaultFirstItem = true,
    initialSelectedOptionId,
    onOptionPick,
    onSearchInput,
    searchReqLoading,
    disableDropDownForSingleItem,
    enableSearch,
    redirectOnItemChange,
    hintToViewOnSelection,
    dataTestId,
    onLoadMore,
    isLoadingMore = false,
    isSortedAlphabetically = true,
    disableDropDown = 'N',
    ariaLabel = '',
    ariaDescription = '',
    initialLoading = false,
    allowAutoComplete = true,
  } = props;
  const dispatch = useDispatch();
  const dropdownData = useSelector(
    (state: RootState) => state.clientReducer[dropDownID]
  );
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const {borderClass, focusHandler, mouseKeyDownHandler} = useFieldFocusStyle({
    tabFocusClass: styles.tabFocusedBorder,
    mouseFocusClass: styles.mouseFocusedBorder,
  });

  const dropDownHintID = useId();
  const searchHintId = useId();
  const open = Boolean(anchorEl);
  const searchBoxVisible: boolean = useMemo(() => {
    return (
      enableSearch ??
      (dropdownData?.options ?? []).length >= PROJECT_LIST_SEARCH_ENABLE_COUNT
    );
  }, [dropdownData, enableSearch]);

  const accessibilityContentPath = useMemo(() => {
    return MapAccessibilityContentKey[dropDownID]
      ? `accessibility.clientDropdown.${MapAccessibilityContentKey[dropDownID]}.`
      : `${ariaLabel} `;
  }, [dropDownID, ariaLabel]);

  const arrowIcon = open ? (
    <DropUpIcon data-testid="drop-down-arrow-up" aria-hidden="true" />
  ) : (
    <DropDownIcon data-testid="drop-down-arrow-down" aria-hidden="true" />
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      if (onSearchInput) onSearchInput(searchTerm);
      else setDebouncedSearchTerm(searchTerm);
    }, 1000);
    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    const formattedData =
      data
        ?.filter(
          (item) => item && item[idKey] != null && item[displayKey] != null
        )
        .map((item) => ({
          id: item[idKey],
          display: item[displayKey],
        })) ?? [];

    dispatch(
      setOptions({
        dropdownId: dropDownID,
        options: formattedData,
      })
    );

    if (dropDownID === CLIENT_DROP_DOWN_ID) {
      let selectedOptionId = null;
      let selectedOptionLable = null;
      /**
       * To avoid selected option reset when search have no result, setting
       * default in redux only when user picked client already or selectDefaultFirstItem
       * is true
       */
      if (initialSelectedOptionId) {
        selectedOptionId = initialSelectedOptionId;
        selectedOptionLable =
          formattedData.find((item) => item.id === initialSelectedOptionId)
            ?.display ?? null;
      } else if (selectDefaultFirstItem) {
        selectedOptionId = formattedData[0]?.id;
        selectedOptionLable = formattedData[0]?.display;
      }

      if (selectedOptionId === null) {
        dispatch(
          selectOption({
            dropdownId: dropDownID,
            selectedOption: null,
            selectedOptionLable: null,
          })
        );
      } else if (selectedOptionId && selectedOptionLable) {
        dispatch(
          selectOption({
            dropdownId: dropDownID,
            selectedOption: selectedOptionId,
            selectedOptionLable: selectedOptionLable,
          })
        );
        onOptionPick && onOptionPick(selectedOptionId);
      }
    } else {
      let selectedOptionId = null;
      let selectedOptionLable = null;

      if (selectDefaultFirstItem) {
        selectedOptionId = formattedData[0]?.id;
        selectedOptionLable = formattedData[0]?.display;
      }

      if (initialSelectedOptionId) {
        const option = formattedData.find(
          (item) => item.id === initialSelectedOptionId
        );
        if (option) {
          selectedOptionId = option.id;
          selectedOptionLable = option.display;
        }
      }

      dispatch(
        selectOption({
          dropdownId: dropDownID,
          selectedOption: selectedOptionId,
          selectedOptionLable: selectedOptionLable,
        })
      );
      if (selectedOptionId) onOptionPick?.(selectedOptionId);
    }
  }, [dispatch, dropDownID, data, displayKey, idKey]);

  useEffect(() => {
    if (initialSelectedOptionId === null) {
      dispatch(
        selectOption({
          dropdownId: dropDownID,
          selectedOption: null,
          selectedOptionLable: null,
        })
      );
    } else {
      const formattedData =
        data?.map((item) => ({
          id: item[idKey],
          display: item[displayKey],
        })) ?? [];
      const option = formattedData.find(
        (item) => item.id === initialSelectedOptionId
      );
      if (option) {
        dispatch(
          selectOption({
            dropdownId: dropDownID,
            selectedOption: option.id,
            selectedOptionLable: option.display,
          })
        );
      }
    }
  }, [initialSelectedOptionId]);

  const filteredDataList = useMemo(() => {
    const filtered = (dropdownData?.options ?? []).filter((item) =>
      item.display?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );

    return isSortedAlphabetically
      ? filtered.sort((a, b) => a.display.localeCompare(b.display))
      : filtered;
  }, [dropdownData, debouncedSearchTerm, isSortedAlphabetically]);

  const handleSelect = (item: any) => {
    onOptionPick?.(item.id);
    dispatch(
      selectOption({
        dropdownId: dropDownID,
        selectedOption: item?.id,
        selectedOptionLable: item.display,
      })
    );
    setSearchTerm('');
    setAnchorEl(null);
    if (redirectOnItemChange) {
      navigate(redirectOnItemChange);
    }
  };

  const handleToggleDropdown = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm('');
  };

  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onLoadMore || !loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingMore) {
          onLoadMore();
        }
      },
      {threshold: 1.0} // Trigger when the red block is fully visible
    );

    observer.observe(loadMoreRef.current);

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [onLoadMore, isLoadingMore, open]);

  const disableDropdownClick = useMemo(() => {
    // Disable the dropdown click when only one item for display and search input is blank
    if (
      disableDropDownForSingleItem &&
      (dropdownData?.options ?? []).length === 1 &&
      !searchTerm
    )
      return true;
    return false;
  }, [disableDropDownForSingleItem, dropdownData, searchTerm]);

  const renderEmptyListMsessage = () => {
    if (
      filteredDataList.length === 0 &&
      searchBoxVisible &&
      dropDownID === CLIENT_DROP_DOWN_ID &&
      !initialLoading
    ) {
      return (
        <Typography
          aria-label={t('common.noResults')}
          data-testid="empty-message-lable"
          className="typo-text body3_size no-result-label"
        >
          {t('common.noResults')}
        </Typography>
      );
    }
  };

  return (
    <div className="aur-client-dropdown">
      <Button
        variant="outlined"
        onClick={(e) => {
          if (disableDropdownClick || disableDropDown === 'Y') {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          handleToggleDropdown(e);
        }}
        endIcon={!disableDropdownClick ? arrowIcon : undefined}
        className={`client-dropdown-button ${
          (buttonBorderHighlight && open && 'open') || ''
        } ${borderClass} ${
          disableDropdownClick || disableDropDown === 'Y'
            ? 'mui-button-disabled'
            : ''
        }`}
        sx={{textTransform: 'none'}}
        tabIndex={0}
        disabled={disableDropdownClick || disableDropDown == 'Y'}
        disableRipple={disableDropdownClick || disableDropDown == 'Y'}
        aria-disabled={disableDropdownClick || disableDropDown == 'Y'}
        aria-haspopup="menu"
        aria-controls={open ? 'drop-down-menu' : undefined}
        aria-expanded={open ? 'true' : 'false'}
        aria-label={`${buttonLabel ?? ''} ${
          dropdownData?.selectedOptionLable ?? ''
        } ${t(
          accessibilityContentPath +
            (dropdownData?.selectedOption
              ? 'buttonWithValueHint'
              : hintToViewOnSelection
                ? 'buttonHintWithContent'
                : 'buttonHint'),
          {
            content: hintToViewOnSelection ?? '',
          }
        )}`}
        data-testid={dataTestId}
        onFocus={focusHandler}
        onMouseDown={mouseKeyDownHandler}
        aria-describedby={
          disableDropdownClick || disableDropDown === 'Y' ? 'btn-info' : ''
        }
      >
        {dropdownData?.selectedOption ? (
          <div className="button-text-div">
            <Typography
              aria-label={buttonLabel}
              className={`typo-text label2_size custom-label`}
            >
              {buttonLabel}
            </Typography>
            <Typography
              data-testid="selected-option-label"
              className={`typo-text subhead3_size custom-button-text ${buttonOrTextField === 'text' ? 'mist-100' : ''}`}
              aria-label={`${t('common.selectClientName')} ${dropdownData.selectedOptionLable ?? ''}`}
            >
              {dropdownData.selectedOptionLable}
            </Typography>
          </div>
        ) : (
          <div className="button-default-label-text-div">
            <Typography aria-label={buttonLabel} className="default-label-text">
              {buttonLabel}
            </Typography>
          </div>
        )}
      </Button>
      <span id={dropDownHintID} style={{display: 'none'}}>
        {t(
          accessibilityContentPath +
            (dropdownData?.selectedOption
              ? 'dropdownWithValueHint'
              : 'dropdownHint'),
          {name: dropdownData?.selectedOptionLable ?? ''}
        )}
      </span>
      <span id={searchHintId} style={{display: 'none'}}>
        {t(accessibilityContentPath + 'searchHint')}
      </span>
      <span
        id="btn-info"
        className="sr-only"
        tabIndex={
          disableDropdownClick || (disableDropDown == 'Y' && ariaDescription)
            ? 0
            : -1
        }
        {...(ariaLabel ? {role: 'region', 'aria-label': ariaLabel} : {})}
        aria-label={ariaLabel}
        aria-disabled={disableDropdownClick || disableDropDown == 'Y'}
      >
        {ariaDescription}
      </span>
      <CustomMenu
        id="drop-down-menu"
        className="aurClientDropDownMenu"
        MenuListProps={{
          'aria-labelledby': dropDownHintID,
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        transformOrigin={{vertical: 'top', horizontal: 'center'}}
        disablePortal
        role="menu"
        disableScrollLock
      >
        {searchBoxVisible && (
          <AurTextField
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="filled"
            className="aur-textfield custom-text-field client-dropDown-search-box"
            onKeyDown={(e) => e.stopPropagation()}
            label={t('userHomePage.search')}
            data-testid="search-input"
            inputProps={{
              'aria-describedby': searchHintId,
              autoComplete: allowAutoComplete ? "on" : "off"
            }}
            ariaLabel={t('userHomePage.search')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchReqLoading ? (
                    <AurLoadingIndicator show size={24} />
                  ) : (
                    <AurIcon
                      icon={IconNames.Search}
                      customClassName="client-drop-down-search-icon"
                      color={IconColors.Custom}
                    />
                  )}
                </InputAdornment>
              ),
            }}
          />
        )}

        {filteredDataList?.map((item) => (
          <MenuItem
            key={item.id}
            data-testid={item.id}
            onClick={() => handleSelect(item)}
            tabIndex={0}
            className={`aurClientDropDownMenuItems ${dropdownData?.selectedOption === item.id ? 'selected' : ''}`}
            aria-label={t(
              item.display +
                (dropdownData?.selectedOption === item.id
                  ? 'listItemSelected'
                  : 'listItem'),
              {
                name: item.display,
              }
            )}
            role="option"
          >
            <Typography className="typo-text body2_size menu-items-text">
              {item.display}
            </Typography>
            {dropdownData?.selectedOption === item.id && (
              <img
                src="/assests/images/icon_checkmark.svg"
                className="check-icon"
                aria-hidden
              />
            )}
          </MenuItem>
        ))}

        {(isLoadingMore ||
          (initialLoading && (dropdownData?.options ?? []).length === 0)) && (
          <MenuItem disabled>
            <AurLoadingIndicator size={20} />
            {t('common.loading')}
          </MenuItem>
        )}

        <div
          ref={loadMoreRef}
          style={{
            opacity: 0,
          }}
        >
          Loading more...
        </div>

        {renderEmptyListMsessage()}
      </CustomMenu>
    </div>
  );
};

export default AurClientDropdown;
